import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else {
      const eventProductId =
        event.detail.data.productId ??
        (event.target instanceof HTMLElement ? event.target.dataset.productId : null);
      if (eventProductId && String(eventProductId) !== String(this.dataset.productId)) {
        return;
      }
    }

    const { priceContainer, volumePricingNote } = this.refs;
    const blockSelector = `product-price[data-block-id="${this.dataset.blockId}"]`;
    const newProductPrice =
      event.detail.data.html.querySelector(`.product-information ${blockSelector}`) ||
      event.detail.data.html.querySelector(blockSelector);
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    const currentPriceContainer = this.querySelector('[ref="priceContainer"]');
    if (newPrice && currentPriceContainer) {
      currentPriceContainer.replaceWith(newPrice);
    }

    // Update per-day pill (sibling of priceContainer, not inside ref)
    const newPerDay = newProductPrice.querySelector('#hc-per-day-price');
    const currentPerDay = this.querySelector('#hc-per-day-price');
    const priceContainerAfter = this.querySelector('[ref="priceContainer"]');

    if (newPerDay && currentPerDay) {
      currentPerDay.replaceWith(newPerDay);
    } else if (newPerDay && !currentPerDay && priceContainerAfter) {
      priceContainerAfter.insertAdjacentElement('afterend', newPerDay);
    } else if (!newPerDay && currentPerDay) {
      currentPerDay.remove();
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
    const currentNote = this.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      currentNote?.remove();
    } else if (!currentNote) {
      const anchor = this.querySelector('[ref="priceContainer"]') || this.querySelector('#hc-per-day-price');
      anchor?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      currentNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
